import { MdKeyboardArrowRight } from "react-icons/md";
import ResponseTable from "./ResponseTable";
import styles from "../style.module.scss";

interface ConversationType {
  conversationId: number;
  askedBy: string;
  askedOn: string | null;
  question: string;
  response: string | null;
  complianceStatus: boolean | null;
  aiValidation: {
    satisfactory: string;
    followupQuestions: string[];
    reason: string;
  } | null;
  responseValidationRequired: boolean;
  reason: string | null;
  questionCode: string;
  documents: { fileName: string }[];
  respondedOn: string | null;
  responseValidatedOn: string | null;
  responseValidatedBy: any | null;
}

interface Auditee {
  name: string;
  conversations: ConversationType[];
}

interface AuditeeAccordionProps {
  auditee: Auditee;
  questionId: number;
  isOpen: boolean;
  onToggle: () => void;
  columns: { label: string; key: string }[];
  responseValidationRequired?: boolean;
  isViewingPastSequence?: boolean;
}

const AuditeeAccordion = ({
  auditee,
  isOpen,
  onToggle,
  columns,
  responseValidationRequired = false,
  isViewingPastSequence = false,
}: AuditeeAccordionProps) => {
  const conversations = auditee.conversations || [];
  const lastConversation = conversations[conversations.length - 1];
  const isAwaiting = !lastConversation?.response;
  const isSatisfied = lastConversation?.complianceStatus === true && lastConversation?.responseValidatedOn != null;

  // Check if compliance status has been determined
  const hasComplianceStatus =
    lastConversation?.complianceStatus !== null &&
    lastConversation?.complianceStatus !== undefined;

  // Determine badge text and style based on compliance status
  const getBadgeInfo = () => {
    if (isAwaiting && conversations.length > 1) {
      return { text: "Awaiting a Followup Response", style: styles.auditeeBadgeAwaiting };
    }

    if (isAwaiting) {
      return { text: "Awaiting Response", style: styles.auditeeBadgeAwaiting };
    }

    // If responseValidationRequired and not yet validated by auditor, show "Pending Auditor Review"
    if (responseValidationRequired && lastConversation?.responseValidatedOn === null && lastConversation?.response) {
      return { text: "Pending Auditor Review", style: styles.auditeeBadgeResponded };
    }

    // If viewing a past sequence (user has moved to next stage)
    if (isViewingPastSequence) {
      // Only show "Satisfied" for users who were validated and satisfied the requirements
      // This indicates they were the ones who allowed progression to next stage
      if (isSatisfied) {
        return { text: "Satisfied", style: styles.auditeeBadgeSatisfied };
      }
      // For other users who answered late or didn't satisfy, don't show any status
      return null;
    }

    // Show Satisfied/Not Satisfied based on complianceStatus
    return isSatisfied
      ? { text: "Satisfied", style: styles.auditeeBadgeSatisfied }
      : { text: "Not Satisfied", style: styles.auditeeBadgeNotSatisfied };
  };

  const badgeInfo = getBadgeInfo();

  return (
    <div style={{ marginBottom: "12px" }}>
      {/* Auditee Header */}
      <div
        className={styles.auditeeHeader}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        onClick={onToggle}
      >
        <div className={styles.auditeeHeaderLeft}>
          <MdKeyboardArrowRight
            size={20}
            style={{
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
          <span className={styles.auditeeName}>{auditee.name}</span>
          {badgeInfo && (
            <span className={`${styles.auditeeBadge} ${badgeInfo.style}`}>
              {badgeInfo.text}
            </span>
          )}
        </div>
      </div>

      {/* Response Table */}
      {isOpen && (
        <ResponseTable conversations={conversations} columns={columns} responseValidationRequired={responseValidationRequired} />
      )}
    </div>
  );
};

export default AuditeeAccordion;
